import { useState } from 'react'

// Beck's 12 cognitive distortions with explanations and examples
export const DISTORTION_INFO: Record<string, {
  name: string
  description: string
  example: string
  reframe: string
}> = {
  'all-or-nothing thinking': {
    name: 'All-or-Nothing Thinking',
    description: 'Seeing things in black and white, with no middle ground. If something isn\'t perfect, it\'s a complete failure.',
    example: '"I made one mistake, so the whole project is ruined."',
    reframe: 'Look for the gray areas. Most situations are a mix of good and bad.'
  },
  'catastrophizing': {
    name: 'Catastrophizing',
    description: 'Expecting the worst possible outcome, blowing things out of proportion.',
    example: '"If I fail this test, I\'ll never get a good job and my life will be ruined."',
    reframe: 'Ask yourself: What\'s the most likely outcome? What would I tell a friend?'
  },
  'mind reading': {
    name: 'Mind Reading',
    description: 'Assuming you know what others are thinking, usually that they\'re judging you negatively.',
    example: '"They didn\'t respond to my text—they must be mad at me."',
    reframe: 'You can\'t read minds. What are other explanations for their behavior?'
  },
  'fortune telling': {
    name: 'Fortune Telling',
    description: 'Predicting the future negatively, as if you know for certain bad things will happen.',
    example: '"I know the presentation will go terribly."',
    reframe: 'Nobody can predict the future. What evidence supports a different outcome?'
  },
  'overgeneralization': {
    name: 'Overgeneralization',
    description: 'Taking one event and assuming it applies to all similar situations. Look for words like "always" or "never."',
    example: '"I got rejected once, so I\'ll always be rejected."',
    reframe: 'One instance doesn\'t define a pattern. What are the exceptions?'
  },
  'labeling': {
    name: 'Labeling',
    description: 'Putting a fixed, global label on yourself or others based on a single action.',
    example: '"I forgot the appointment. I\'m such an idiot."',
    reframe: 'Separate the behavior from your identity. One action doesn\'t define you.'
  },
  'should statements': {
    name: 'Should Statements',
    description: 'Using "should," "must," or "ought" to set rigid rules that lead to guilt or frustration.',
    example: '"I should be further along in my career by now."',
    reframe: 'Replace "should" with "I prefer" or "I would like." Accept what is.'
  },
  'discounting the positive': {
    name: 'Discounting the Positive',
    description: 'Dismissing positive experiences as not counting or being a fluke.',
    example: '"They only complimented me because they were being nice."',
    reframe: 'Positive experiences count! Let yourself accept the good.'
  },
  'emotional reasoning': {
    name: 'Emotional Reasoning',
    description: 'Believing that because you feel something, it must be true.',
    example: '"I feel stupid, so I must be stupid."',
    reframe: 'Feelings aren\'t facts. What does the evidence actually show?'
  },
  'personalization': {
    name: 'Personalization',
    description: 'Taking responsibility for things outside your control, blaming yourself for external events.',
    example: '"The team failed because of me."',
    reframe: 'Consider all factors involved. What was actually in your control?'
  },
  'magnification/minimization': {
    name: 'Magnification/Minimization',
    description: 'Making negatives bigger and positives smaller than they really are.',
    example: '"My small mistake was huge, but my success was just luck."',
    reframe: 'Try to see things in proportion. Give yourself credit for wins.'
  },
  'mental filter': {
    name: 'Mental Filter',
    description: 'Focusing only on the negative aspects while ignoring the positives.',
    example: '"99 people liked my talk, but 1 person frowned—it was a disaster."',
    reframe: 'Zoom out. What\'s the full picture including the positives?'
  }
}

interface DistortionInfoProps {
  distortions: string[]
}

export function DistortionInfo({ distortions }: DistortionInfoProps) {
  const [expandedDistortion, setExpandedDistortion] = useState<string | null>(null)

  if (distortions.length === 0) return null

  return (
    <div className="distortion-info">
      <p className="detail-label" style={{ marginBottom: '0.5rem' }}>
        Cognitive Distortions
        <span style={{
          fontSize: '0.7rem',
          color: 'var(--color-text-muted)',
          fontWeight: 'normal',
          marginLeft: '0.5rem'
        }}>
          (tap to learn)
        </span>
      </p>

      <div className="distortion-tags">
        {distortions.map(d => {
          const info = DISTORTION_INFO[d.toLowerCase()]
          const isExpanded = expandedDistortion === d

          return (
            <div key={d}>
              <button
                onClick={() => setExpandedDistortion(isExpanded ? null : d)}
                className="distortion-tag"
                style={{
                  cursor: 'pointer',
                  border: isExpanded ? '1px solid var(--color-primary)' : '1px solid transparent',
                  transition: 'all 0.2s'
                }}
              >
                {d}
                <span style={{ marginLeft: '0.25rem', fontSize: '0.7rem' }}>
                  {isExpanded ? '▲' : '▼'}
                </span>
              </button>

              {isExpanded && info && (
                <div style={{
                  marginTop: '0.5rem',
                  marginBottom: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-bg)',
                  borderRadius: '0.5rem',
                  borderLeft: '3px solid var(--color-primary)',
                  fontSize: '0.8rem'
                }}>
                  <p style={{
                    fontWeight: '500',
                    color: 'var(--color-primary)',
                    marginBottom: '0.5rem'
                  }}>
                    {info.name}
                  </p>
                  <p style={{
                    color: 'var(--color-text)',
                    marginBottom: '0.5rem',
                    lineHeight: '1.4'
                  }}>
                    {info.description}
                  </p>
                  <p style={{
                    color: 'var(--color-text-muted)',
                    fontStyle: 'italic',
                    marginBottom: '0.5rem'
                  }}>
                    {info.example}
                  </p>
                  <p style={{
                    color: 'var(--color-success)',
                    fontSize: '0.75rem'
                  }}>
                    💡 {info.reframe}
                  </p>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Simple modal version for a more detailed view
interface DistortionModalProps {
  distortion: string
  onClose: () => void
}

export function DistortionModal({ distortion, onClose }: DistortionModalProps) {
  const info = DISTORTION_INFO[distortion.toLowerCase()]

  if (!info) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        zIndex: 100
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-bg-light)',
          padding: '1.5rem',
          borderRadius: '1rem',
          maxWidth: '400px',
          width: '100%'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '1rem'
        }}>
          <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>
            {info.name}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
              fontSize: '1.5rem',
              lineHeight: 1,
              padding: 0
            }}
          >
            ×
          </button>
        </div>

        <p style={{
          marginBottom: '1rem',
          lineHeight: '1.5',
          color: 'var(--color-text)'
        }}>
          {info.description}
        </p>

        <div style={{
          padding: '0.75rem',
          backgroundColor: 'var(--color-bg)',
          borderRadius: '0.5rem',
          marginBottom: '1rem'
        }}>
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--color-text-muted)',
            marginBottom: '0.25rem'
          }}>
            Example thought:
          </p>
          <p style={{
            fontStyle: 'italic',
            color: 'var(--color-text)'
          }}>
            {info.example}
          </p>
        </div>

        <div style={{
          padding: '0.75rem',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          borderRadius: '0.5rem',
          borderLeft: '3px solid var(--color-success)'
        }}>
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--color-success)',
            marginBottom: '0.25rem'
          }}>
            How to reframe:
          </p>
          <p style={{ color: 'var(--color-text)' }}>
            {info.reframe}
          </p>
        </div>
      </div>
    </div>
  )
}
